<template>
	<main class="survey content" :class="{'has-progress-bar': totalPages > 1 && showProgressBar}">
		<div class="page-header" v-if="style && style.logo && onSurveyPage">
			<div class="logo-container">
				<img :src="style.logo" class="logo" alt="Survey logo header" />
			</div>
		</div>
		<div v-if="loading">
		</div>
		<div v-if="surveyNotAvailable" class="survey-unavailable">
			<div class="page-contents">
				<a class="inline-flex mb-12" href="https://medley.so" target="_blank">
					<img src="/static/images/logo.svg" width="36" class="text-hdl-purple" alt="Medley's logo" />
				</a>
				<h2 class="mb-4 text-xl">This survey is currently not available.</h2>
				<div class="mb-8 text-center" v-if="showPoweredBy">Made with Medley, a fast and simple way to collect feedback.</div>
				<a class="inline-flex create-your-own mb-12" href="https://medley.so" target="_blank" v-if="showPoweredBy">
					Create your own survey
				</a>
			</div>
		</div>
		<div v-else-if="onPasswordPage && !surveyNotAvailable" class="survey-password">
			<div class="page-contents">
				<h1 class="mb-1 text-xl font-semibold">Password required</h1>
				<div class="mb-4">Please enter it below.</div>
				<div class="mb-4">
					<label class="font-semibold mb-1 block" for="survey-password">Password</label>
					<hdl-textbox
						v-model="surveyPassword"
						password
						input-id="survey-password"
						@enter="submitPassword"
						class="w-full"
						focus-on-mount
						:aria-describedby="(passwordErrorMessage !== null)? 'survey-password-errors' : ''"
					/>
					<div class="errors" id="survey-password-errors" v-if="passwordErrorMessage !== null" aria-live="assertive">
						{{ passwordErrorMessage }}
					</div>
				</div>
				<div>
					<hdl-button
						label="Continue"
					 	hdl-style="primary"
						class="w-full"
						type="submit"
						:disabled="surveyPassword.trim().length === 0"
						:is-loading="authenticating"
						@click.stop="submitPassword"
						sr-preface="Submit the password and proceed to the survey"
					/>
				</div>
			</div>
		</div>
		<div v-else-if="onEndPage && !surveyNotAvailable" class="survey-complete">
			<div class="page-contents">
				<!--<a class="inline-flex mb-12" href="https://medley.so" target="_blank" aria-label="Link to Medley, the survey tools website, opens in a new tab">
					<img src="/static/images/logo.svg" width="36" class="text-hdl-purple" alt="Medley's logo" />
				</a>-->
				<font-awesome-icon
					:icon="endPage.logo"
					class="inline-flex mb-12 text-4xl"
					v-if="endPage && endPage.logo"
				/>
				<h1 class="mb-4 text-xl">
					<template v-if="endPage === null">
						Thank you for your response!
					</template>
					<template v-else>
						{{ endPage.heading }}
					</template>
				</h1>
				<div class="mb-8 text-center">
					<template v-if="endPage === null">
						Made with Medley, a fast and simple way to collect feedback.
					</template>
					<template v-else>
						<div v-html="endPageContent" />
					</template>
				</div>
				<a
					class="inline-flex create-your-own"
					href="https://medley.so?utm_source=medley&utm_medium=survey&utm_campaign=end"
					target="_blank"
					aria-label="Create a survey using Medley, opens in a new tab"
					v-if="showPoweredBy"
				>
					Create your own survey
				</a>
				<div class="flex flex-row items-center mt-12">
					<!--<hdl-button
						label="Back"
						@click.prevent="onEndPage=false"
						hdl-style="secondary"
						class="mr-2"
					/>-->
					<a
						@click.prevent="backToStart"
						hdl-style="secondary"
						href="#"
					>Back to start</a>
				</div>
			</div>
		</div>
		<div
			v-if="!loading && page && publishedSurvey && !surveyNotAvailable"
			class="survey-page"
			:class="{
				'with-header': style && style.logo
			}"
			v-show="!onEndPage"
		>
			<survey-progress
				:progress="onPage"
				:total="totalPages"
				v-if="totalPages > 1 && showProgressBar"
			/>
			<div class="hdl-new-version-available flex flex-row items-start p-4" aria-live="assertive" v-if="showNewVersionAvailable">
				<font-awesome-icon :icon="['fas', 'circle-exclamation']" fixed-width class="flex-none mr-4 mt-1" />
				<div class="flex-1">
					A new version of this survey has been published. <a :href="versionResetLink">Click here to clear your responses and see the latest version</a>.
				</div>
			</div>
			<div
				class="page-contents hdl-nss-contents"
				role="form"
				:aria-label="`Page ${onPage} of ${totalPages} for survey`"
			>
				<div
					v-html="page.renderedPage"
					ref="content"
				/>
				<div class="end flex flex-row items-center" @keydown="buttonNavKeydown" ref="navButtons" role="navigation">
					<hdl-button
						:label="page.previousPageLabel"
						hdl-style="survey-basic"
						class="mr-2"
						tabindex=0
						size="lg"
						:aria-label="page.previousPageLabel"
						@click.prevent="goBack"
						v-if="backAvailable"
					/>
					<hdl-button
						:label="continueLabel"
						hdl-style="survey-primary"
						:is-loading="submittingPage"
						@click.prevent="submitPage"
						tabindex=0
						size="lg"
						ref="continueButton"
						:arial-label="continueLabel"
						:sr-preface="continueSrPreface"
						type="submit"
					/>
				</div>
			</div>
			<div class="hdl-badge-container flex flex-row" v-if="showPoweredBy">
				<a class="inline-flex flex-row items-center hdl-powered-badge" href="https://medley.so?utm_source=medley&utm_medium=survey&utm_campaign=bottom" target="_blank">
					<svg role="presentation" width="18" height="18" viewBox="0 0 314 314" class="flex-none mr-2" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path fill-rule="evenodd" clip-rule="evenodd" d="M27.3413 248.343C26.1431 266.061 13.9614 276.156 4.17611 283.573C-5.50935 290.99 4.17611 294.699 7.97039 295.317C29.538 299.231 51.6049 296.553 71.7745 287.797C73.6717 286.973 77.0666 288.106 81.0606 290.063C120.401 315.507 168.829 320.967 212.563 305C291.844 276.053 333.282 186.329 305.224 104.639C277.166 22.95 190.396 -19.9035 111.115 9.04323C31.8345 37.9899 -9.60321 127.714 18.4546 209.404V209.507C19.7527 213.215 21.1506 216.924 22.7482 220.529C25.3443 228.255 27.9404 238.866 27.3413 248.343ZM223.528 164.5C223.528 199.018 195.546 227 161.028 227C126.51 227 98.5283 199.018 98.5283 164.5C98.5283 164.333 98.5289 164.167 98.5302 164H71.5283C71.5283 213.706 111.823 254 161.528 254C211.234 254 251.528 213.706 251.528 164H223.526C223.528 164.167 223.528 164.333 223.528 164.5Z" fill="currentColor"/>
					</svg>
					<div><span class="hdl-sr-only">Click here to go to Medley's site, the tool this survey was made with. Opens in a new tab.</span>Powered by Medley</div>
				</a>
			</div>
		</div>
	</main>
</template>

<script lang="ts">
import { FetchResult, KnownFetchResult, fetcher } from '@/common/fetcher';
import {
	PublishedSurveyPageType,PublishedSurveyType, PublishedSurveyQuestionType, QuestionType,
	SubmittedPageResults, SurveyResponseSubmittedAction, SurveyResponseInputErrorType,
	ResponseGroupType, ResponseGroupCompletion,
	PublishedSurveyStyleType,
	SurveyEndPageType,
	SurveyTakeActionType,
	SurveyTakeAction,
	SessionResponseUpdate,
	SubResponseType
} from '@/common/interfaces';

import { AppContext, createVNode, getCurrentInstance } from '@vue/runtime-core';
import { Component, Vue, Prop} from 'vue-facing-decorator';

import SurveyProgress from '../components/SurveyProgress.vue';

import ShortTextResponse from '../components/questions/ShortTextResponse.vue';
import TextResponse from '../components/questions/TextResponse.vue';
import SingleChoice from '../components/questions/SingleChoice.vue';
import MultipleChoice from '../components/questions/MultipleChoice.vue';
import Nps from '../components/questions/Nps.vue';
import Rating from '../components/questions/Rating.vue';
import StarRating from '../components/questions/StarRating.vue';
import DropdownQuestion from '../components/questions/DropdownQuestion.vue';
import Matrix from '../components/questions/Matrix.vue';

import { render } from 'vue';
import { ResponseGroup } from '../responses';
import { useResponseStore } from '../stores/responseStore';
import { assertUnreachable } from '@/common/utils';
import { IQuestionComponent } from '../components/questions/types';
import HdlButton from '@/components/common/HdlButton.vue';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faCircleExclamation } from '@fortawesome/pro-solid-svg-icons';

import { SurveyLogicRule } from "@/surveys/logic/types";

import { LogicHandler, QuestionEffect } from '@/respondants/logic';

import '@/common/surveys/icons';
import { MedleyWindowMessage, MedleyWindowMessageData } from '@/medleyEmbed/types';
import Ranking from '../components/questions/Ranking.vue';

library.add(faCircleExclamation);

declare type LocalQuestionResponse = {
	questionUuid: string;
	questionType: QuestionType;
	text: string | null;
	value: number | null;
	choices: Array<{choiceUuid: string, text?: string | null}> | null;
	subResponses: Array<SubResponseType> | null;
};

declare type SessionResponseInfo = {[key: string]: {sessionKey: string}};

@Component({
	components: {
		SurveyProgress
	}
})
export default class PublishedSurvey extends Vue {
	@Prop({ required: true })
	slug: string;

	surveyUuid: string | null = null;
	publishedSurvey: PublishedSurveyType | null = null;

	page: PublishedSurveyPageType | null = null;

	style: PublishedSurveyStyleType | null = null;

	totalPages: number = 1;

	onPage: number = 1;

	loading: boolean = false;
	loadingPage: boolean = false;

	submittingPage: boolean = false;

	submitted: boolean = false;
	onEndPage: boolean = false;

	authenticating: boolean = false;
	surveyPassword: string = "";
	onPasswordPage: boolean = false;
	passwordErrorMessage: string | null = null;

	showPoweredBy: boolean = true;
	showProgressBar: boolean = true;

	pagePath: Array<string>;

	// Left off of here so that it's not made reactive, left commented for doc
	//appContext: AppContext;

	hasNextPage: boolean = false;

	usingKeyboardShortcuts: boolean = false;

	surveyNotAvailable: boolean = false;

	loadedQuestionsVnodes: {[key: string]: [ReturnType<typeof createVNode>, HTMLDivElement]};

	showNewVersionAvailable: boolean = false;

	logicHandler: LogicHandler;

	seenQuestionInfo: Array<PublishedSurveyQuestionType>;

	endPage: SurveyEndPageType | null = null;

	// For resize notifications
	resizeInfo: {width: number; height: number} = {width: 0, height: 0};

	mounted(): void {
		this.logicHandler = new LogicHandler();
		//@ts-ignore
		this.logicHandler.events.on('question:effect', this.questionEffectChanged.bind(this));

		this.pagePath = [];
		this.loadedQuestionsVnodes = {};
		//@ts-ignore
		this.appContext = getCurrentInstance()!.appContext;

		const paramPage = this.$route.query.p;

		if (paramPage !== undefined) {
			const intPage = parseInt(paramPage as string);
			if (!isNaN(intPage)) {
				this.onPage = intPage;
			}
		}

		this.fetchSurvey();

		if (this.isEmbedded) {
			if (this.$route.query['mdl-transparent-bg'] == '1') {
				window.document.body.classList.add('mdl-survey-transparent');
			}

			if (this.$route.query['mdl-no-spacing'] == '1') {
				window.document.body.classList.add('mdl-survey-no-spacing');
			}

			if (this.$route.query['mdl-auto-height'] == '1') {
				const resizeObserver = new ResizeObserver(
					(entries) => {
						this.notifyLayoutUpdate();
					}
				);
				resizeObserver.observe(this.$el);
			}
		}
	}

	postEmbedMessage(messageData: MedleyWindowMessage): void {
		if (!this.isEmbedded || window.top === null || this.embedId === null) {
			return; // Not embedded
		}

		const data: MedleyWindowMessageData = { id: this.embedId, ...messageData };

		let targetOrigin: string | null = this.$route.query['mdl-emd-fo'] as string;

		if (targetOrigin === 'srcdoc' || targetOrigin === 'file://') {
			targetOrigin = '*';
		}

		window.parent.postMessage(JSON.stringify(data), targetOrigin);
	}

	questionEffectChanged(info: {uuid: string, effect: QuestionEffect}): void {
		if (this.page === null) {
			return;
		}

		for (const question of this.page.questions) {
			if (question.uuid === info.uuid) {
				question.visible = info.effect.visible;

				if (!question.visible && this.responseGroup) {
					this.responseGroup.clearResponse(question.uuid);
				}

				const questionVNode = this.loadedQuestionsVnodes[question.uuid];
				if (questionVNode) {
					questionVNode[0].props!.question = question;
					questionVNode[0].component!.update();
				}

				break;
			}
		}
	}

	submitPassword(): void {
		if (this.surveyPassword.trim().length === 0) {
			return;
		}

		this.authenticating = true;
		fetcher.Post(
			`/api/surveys/${this.slug}/take/password/`,
			{ password: this.surveyPassword }
		).then(
			(result: FetchResult) => {
				this.authenticating = false;
				if (result.response.ok && result.json.success) {
					this.fetchSurvey();
					return;
				} else if (result.response.ok && !result.json.success) {
					if (result.json.code === 'SAP-001') {
						this.passwordErrorMessage = "The password provided was not valid.";
					}
				}
			}
		);
	}

	fetchSurvey(): void {
		this.loading = true;
		this.loadingPage = true;

		fetcher.Get(
			`/api/surveys/${this.slug}/take/${window.location.search}`,
			{
				headers: {
					'MEDLEY-RESPONSE-SESSION': this.getSessionKey(this.slug)
				}
			}
		).then(
			this.surveyFetched
		);
	}

	configureStyle(): void {
		if (this.style && this.style.styling && this.style.styling.colors && this.style.styling.colors.background) {
			document.body.style.backgroundColor = this.style.styling.colors.background;
		}
	}

	surveyFetched(result: KnownFetchResult<{
		available: true,
		action?: SurveyTakeActionType;
		showVersionWarning: boolean,
		showPoweredBy: boolean,
		showProgressBar: boolean,
		publishedSurvey: PublishedSurveyType,
		onPage: PublishedSurveyPageType,
		totalPages: number,
		responseGroup: ResponseGroupType,
		hasNextPage: boolean,
		style: PublishedSurveyStyleType | null,
		customCss: string | null,
		surveyUuid: string,
		sessionUpdate: SessionResponseUpdate,
		seenPages: Array<PublishedSurveyPageType>
	} | { available: false, reason?: string }>): void {
		if (result.response.ok && result.json.success) {
			if (result.json.data.available) {
				this.onPasswordPage = false;
				this.surveyNotAvailable = false;
				this.showNewVersionAvailable = result.json.data.showVersionWarning;
				this.publishedSurvey = result.json.data.publishedSurvey;
				this.showPoweredBy = result.json.data.showPoweredBy;
				this.showProgressBar = result.json.data.showProgressBar;
				this.surveyUuid = result.json.data.surveyUuid;
				this.hasNextPage = result.json.data.hasNextPage;
				this.totalPages = result.json.data.totalPages;
				this.style = result.json.data.style;

				for (const seenPage of result.json.data.seenPages) {
					this.responseStore.addSeenPage(seenPage);
				}

				this.updateResponseSession(result.json.data.sessionUpdate);
				this.configureStyle();

				if (result.json.data.customCss) {
					const customCssEl = document.createElement('style') as HTMLStyleElement;
					customCssEl.setAttribute('type', 'text/css');
					customCssEl.innerHTML = result.json.data.customCss;
					document.body.appendChild(customCssEl);
				}

				this.logicHandler.setLogic(
					result.json.data.publishedSurvey.logic as Array<SurveyLogicRule> | null
				);

				this.updateResponseGroup(result.json.data.responseGroup);

				if (result.json.data.responseGroup.completion === ResponseGroupCompletion.COMPLETE) {
					this.submitted = true;
					this.onEndPage = true;
				}

				if (this.page === null) {
					this.setPage(result.json.data.onPage);
				}

				if (result.json.data.action) {
					const action: SurveyTakeActionType = result.json.data.action;

					if (action.action === SurveyTakeAction.JUMP_TO_END_PAGE && action.targetInfo) {
						const endPage: SurveyEndPageType = action.targetInfo.endPage;
						this.endPage = endPage;
						this.onEndPage = true;
					}
				}
			} else {
				const reason: string | undefined = result.json.data.reason;
				if (reason === 'auth_required') {
					this.onPasswordPage = true;
				} else {
					this.surveyNotAvailable = true;
					this.onPasswordPage = false;
				}
			}
		}

		this.loading = false;
		this.loadingPage = false;
	}

	updateResponseGroup(responseGroup: ResponseGroupType): void {
		if (this.responseGroup === null) {
			this.setResponseGroupFromRemote(responseGroup);
			return;
		}

		if (responseGroup.uuid === this.responseGroup.uuid) {
			this.responseStore.updateFromRemote(responseGroup);
		} else {
			// TODO figure out how why this scenario would happen
			this.setResponseGroupFromRemote(responseGroup);
		}
	}

	setResponseGroupFromRemote(responseGroup: ResponseGroupType): void {
		this.responseStore.setFromRemote(responseGroup);

		if (this.responseGroup) {
			this.responseGroup.events.on('responsegroup:updated', this.logicHandler.evaluateAll.bind(this.logicHandler));
			//@ts-ignore
			this.responseGroup.events.on('response:updated', this.logicHandler.responseChanged.bind(this.logicHandler));
		} else {
			console.warn("Response group unavailable in store after set.");
		}
	}

	buttonNavKeydown(event: KeyboardEvent): void {
		if (this.page === null) {
			return;
		}

		this.usingKeyboardShortcuts = true;

		const navButtonsContainer = this.$refs.navButtons as HTMLDivElement | undefined;

		if (event.key === 'ArrowLeft' && navButtonsContainer && navButtonsContainer.contains(document.activeElement)) {
			const focusedIndex = Array.prototype.indexOf.call(navButtonsContainer.children, document.activeElement);

			if (focusedIndex < 1) {
				return;
			}

			(navButtonsContainer.children[focusedIndex - 1] as HTMLElement).focus();
		} else if (event.key === 'ArrowRight' && navButtonsContainer && navButtonsContainer.contains(document.activeElement)) {
			const focusedIndex = Array.prototype.indexOf.call(navButtonsContainer.children, document.activeElement);

			if (focusedIndex < 0 || focusedIndex > navButtonsContainer.children.length - 1) {
				return;
			}

			(navButtonsContainer.children[focusedIndex + 1] as HTMLElement).focus();
		} else if (event.key === 'ArrowUp') {
			event.preventDefault();
			event.stopPropagation();
			this.focusQuestion(this.page.questions[this.page.questions.length-1].uuid, 'below');
		}
	}

	focusSubmit(): void {
		const submit = this.$refs.continueButton as HdlButton | undefined;

		if (submit) {
			submit.$el.focus();
		}
	}

	backToStart(): void {
		if (this.surveyUuid === null || this.page === null || this.responseGroup === null) {
			return;
		}

		fetcher.Get(
			`/api/surveys/${this.slug}/navigate/${this.responseGroup.publishedSurveyUuid}/back/?fp=1`,
			{
				headers: {
					'MEDLEY-RESPONSE-SESSION': this.getSessionKey(this.slug)
				}
			}
		).then(
			(result: FetchResult) => {
				if (result.response.ok && result.json.success) {
					if (result.json.data.previousPage) {
						this.setPage(result.json.data.previousPage);
						this.hasNextPage = true;
					}
				}
			}
		);
	}

	goBack(): void {
		if (this.surveyUuid === null || this.page === null || this.responseGroup === null) {
			return;
		}

		fetcher.Get(
			`/api/surveys/${this.slug}/navigate/${this.responseGroup.publishedSurveyUuid}/back/?page=${encodeURIComponent(this.page.uuid)}`,
			{
				headers: {
					'MEDLEY-RESPONSE-SESSION': this.getSessionKey(this.slug)
				}
			}
		).then(
			(result: FetchResult) => {
				if (result.response.ok && result.json.success) {
					if (result.json.data.previousPage) {
						this.setPage(result.json.data.previousPage);
						this.hasNextPage = true;
					}
				}
			}
		);
	}

	setPage(page: PublishedSurveyPageType): void {
		this.responseStore.addSeenPage(page);

		if (this.page !== null && page.uuid === this.page.uuid) {
			this.onEndPage = false;
			return;
		}

		if (this.page !== null) {
			// We need to clean up the existing vnodes before loading in new ones
			for (const [uuid, [qVNode, qDom]] of Object.entries(this.loadedQuestionsVnodes)) {
				render(null, qDom);
				delete this.loadedQuestionsVnodes[uuid];
			}
			this.loadedQuestionsVnodes = {};
		}

		this.onEndPage = false;

		this.page = page;

		this.onPage = page.index + 1;

		this.pagePath.push(page.uuid);

		this.logicHandler.evaluateAll();

		this.$nextTick(() => {	// Why does it take 2? 1 for the v-if then 1 for the v-html?
			this.$nextTick(() => {
				this.initializeQuestions(page.questions);

				window.scrollTo(0, 0);

				if (this.usingKeyboardShortcuts) {
					this.$nextTick(() => {
						if (page.questions[0] !== undefined) {
							this.focusQuestion(page.questions[0].uuid, 'above');
						}
					});
				}
			});
		});
	}

	submitPage(): void {
		if (this.surveyUuid === null || this.page === null || this.publishedSurvey === null || this.responseGroup === null) {
			return;
		}

		const pageResponses: {[questionUuid: string]: LocalQuestionResponse} = {};

		for (const question of this.page.questions) {
			const response = this.responseGroup.getResponse(question.uuid);
			if (response) {
				// We don't want to count "" as a response to Text type questions
				//	because the response store is agnostic to question type we
				//	strip it here
				if ([
					QuestionType.SHORT_TEXT_RESPONSE, QuestionType.TEXT_RESPONSE
				].includes(question.questionType)) {
					if (response.text === null || response.text.trim().length === 0) {
						continue;
					}
				}

				pageResponses[question.uuid] = {
					questionType: question.questionType,
					questionUuid: question.uuid,
					text: response.text,
					value: response.value,
					choices: response.choices,
					subResponses: response.subResponses
				};
			}
		}

		if (pageResponses === undefined) {
			console.warn("No responses to submit");
			return;
		}

		this.submittingPage = true;
		fetcher.Post(
			`/api/surveys/${this.slug}/submit/${this.responseGroup.publishedSurveyUuid}/`,
			{
				page: this.page.uuid,
				responses: pageResponses
			},
			{
				headers: {
					'MEDLEY-RESPONSE-SESSION': this.getSessionKey(this.slug)
				}
			}
		).then(
			this.pageSubmitted
		);
	}

	pageSubmitted(result: KnownFetchResult<SubmittedPageResults>): void {
		if (result.response.ok && result.json.success) {
			if (result.json.data.endPage) {
				this.endPage = result.json.data.endPage;
			}

			this.handlePageResponse(result.json.data);
		}

		this.submittingPage = false;
	}

	updateResponseSession(sessionUpdate: SessionResponseUpdate | null): void {
		if (sessionUpdate === null) {
			return; // No change required
		}

		const sessionInfoSerialized = localStorage.getItem('responseSession');
		let sessionInfo: SessionResponseInfo | null = null;

		if (sessionInfoSerialized !== null){
			try {
				sessionInfo = JSON.parse(sessionInfoSerialized);
			} catch(e) {
				sessionInfo = null;
			}
		}

		if (sessionInfo === null) {
			sessionInfo = {};
		}

		switch(sessionUpdate.action) {
			case 'upsert':
				if (sessionUpdate.sessionKey) {
					sessionInfo[sessionUpdate.surveyKey] = {sessionKey: sessionUpdate.sessionKey};
				}
			break;
			case 'remove':
				delete sessionInfo[sessionUpdate.surveyKey];
			break;
			case 'add': // Only add if not already existing
				if (sessionUpdate.sessionKey && sessionInfo[sessionUpdate.surveyKey] === undefined) {
					sessionInfo[sessionUpdate.surveyKey] = {sessionKey: sessionUpdate.sessionKey};
				}
			break;
		}

		localStorage.setItem('responseSession', JSON.stringify(sessionInfo));
	}

	getSessionKey(surveyKey: string): string | null {
		const responseSessionInfo = localStorage.getItem('responseSession');

		if (responseSessionInfo === null) {
			return null;
		}

		let sessionInfo: SessionResponseInfo;
		try {
			sessionInfo = JSON.parse(responseSessionInfo);
		} catch(e) {
			return null;
		}

		const result = sessionInfo[surveyKey];

		if (result === undefined) {
			return null;
		}

		return result.sessionKey;
	}

	handlePageResponse(result: SubmittedPageResults): void {
		this.updateResponseSession(result.sessionUpdate);

		if (result.isLastPage) {
			this.hasNextPage = false;
		}

		switch(result.action) {
			case SurveyResponseSubmittedAction.GO_TO_NEXT:
				if (result.nextPage) {
					this.setPage(result.nextPage);
				} else {
					console.error("Missing page on gotonext")
				}
				break;
			case SurveyResponseSubmittedAction.DONE:
				this.submitted = true;

				this.postEmbedMessage({
					event: 'completed'
				});

				if (result.redirectOnCompleteUrl) {
					window.location.href = result.redirectOnCompleteUrl;
					return;
				}

				this.onEndPage = true;
				break;
			case SurveyResponseSubmittedAction.STAY_ERRORS:
				if (result.inputErrors) {
					this.clearAllQuestionErrors();
					this.flagInvalidInputs(result.inputErrors);
				}
				break;
		}
	}

	clearAllQuestionErrors(): void {
		//@ts-ignore
		for (const loadedVnode of Object.values(this.loadedQuestionsVnodes)) {
			//@ts-ignore
			if (loadedVnode[0].component) {
				//@ts-ignore
				loadedVnode[0].component.proxy.errorMap.clear();
			}
		}
	}

	flagInvalidInputs(errors: Array<SurveyResponseInputErrorType>): void {
		for (const error of errors) {
			//@ts-ignore
			this.loadedQuestionsVnodes[error.questionUuid][0].component.proxy.markError(error);
		}
	}

	questionIntentionalBlur(el: HTMLElement, question: PublishedSurveyQuestionType, direction: 'up' | 'down'): void {
		if (this.page === null) {
			return;
		}

		const qIndex = this.page.questions.findIndex(
			(q: PublishedSurveyQuestionType) => q.uuid === question.uuid
		);

		let focusQuestionUuid: string | null = null;
		if (direction === 'up' && qIndex > 0) {
			focusQuestionUuid = this.page.questions[qIndex-1].uuid;
		} else if (direction === 'down' && qIndex < this.page.questions.length - 1) {
			focusQuestionUuid = this.page.questions[qIndex+1].uuid;
		}

		if (focusQuestionUuid) {
			this.focusQuestion(focusQuestionUuid, (direction === 'up')? 'below' : 'above');
		} else if (qIndex === this.page.questions.length - 1) {
			this.focusSubmit();
		}
	}

	focusQuestion(questionUuid: string, fromDirection: 'above' | 'below'): void {
		const [vNode, el] = this.loadedQuestionsVnodes[questionUuid];

		if (vNode === undefined) {
			return;
		}

		const elVue = (el.children[0] as HTMLElement & {__vue__?: IQuestionComponent}).__vue__;

		if (elVue) {
			elVue.focusFrom(fromDirection);
		}
	}

	initializeQuestions(questions: Array<PublishedSurveyQuestionType>): void {
		const surveyContent = this.$refs.content as HTMLDivElement | undefined;

		if (surveyContent === undefined) {
			return;
		}

		for (const question of questions) {
			const questionPlaceholder = surveyContent.querySelector(`div[data-question-ph-uuid="${question.uuid}"]`);

			if (questionPlaceholder === null) {
				console.warn("Question was already loaded, or is missing");
				continue;
			}

			const questionDom = document.createElement('div');

			let questionComponent: any | null = null;

			switch(question.questionType) {
				case QuestionType.SHORT_TEXT_RESPONSE:
					questionComponent = ShortTextResponse;
				break;
				case QuestionType.TEXT_RESPONSE:
					questionComponent = TextResponse;
				break;
				case QuestionType.SINGLE_CHOICE:
					questionComponent = SingleChoice;
				break;
				case QuestionType.MULTIPLE_CHOICE:
					questionComponent = MultipleChoice;
				break;
				case QuestionType.NPS:
					questionComponent = Nps;
				break;
				case QuestionType.RATING:
					questionComponent = Rating;
				break;
				case QuestionType.STAR_RATING:
					questionComponent = StarRating;
				break;
				case QuestionType.DROPDOWN:
					questionComponent = DropdownQuestion;
				break;
				case QuestionType.MATRIX:
					questionComponent = Matrix;
				break;
				case QuestionType.RANKING:
					questionComponent = Ranking;
				break;
				default:
					assertUnreachable(question.questionType);
				break;
			}

			if (questionComponent === null) {
				console.warn("Failed to find renderer for question type");
				continue;
			}

			const questionVNode = createVNode(
				questionComponent,
				{
					question,
					onIntentionalBlur: this.questionIntentionalBlur.bind(this)
				}
			);

			//@ts-ignore
			questionVNode.appContext = { ...this.appContext };

			if (question.uuid in this.loadedQuestionsVnodes) {
				// This shouldn't ever happen due to the placholder check above
				//	but on the odd chance it does
				render(null, this.loadedQuestionsVnodes[question.uuid][1]);
				delete this.loadedQuestionsVnodes[question.uuid];
			}
			this.loadedQuestionsVnodes[question.uuid] = [questionVNode, questionDom];

			render(questionVNode, questionDom);

			const container = questionPlaceholder.parentElement!;
			container.insertBefore(questionDom, questionPlaceholder)
			container.removeChild(questionPlaceholder);
		}
	}

	notifyLayoutUpdate(): void {
		const surveyBox = window.document.body.querySelector('main.survey')!.getBoundingClientRect();
		const width = surveyBox.width;
		const height = surveyBox.height + surveyBox.top + 10;

		if (width === this.resizeInfo.width && height === this.resizeInfo.height) {
			return;
		}

		this.resizeInfo = { width, height };

		this.postEmbedMessage({
			event: 'resize',
			height: height,
			width: width
		});
	}

	get versionResetLink(): string {
		const resetUrl = new URL(window.location.href);
		resetUrl.searchParams.delete('reset');
		resetUrl.searchParams.append('reset', '1');

		return resetUrl.href;
	}

	get backAvailable(): boolean {
		return this.onPage > 1;
	}

	get continueSrPreface(): string {
		if (this.page && this.onPage < this.totalPages) {
			return "Using this button will submit this page and move onto the next.";
		}

		return (this.hasNextPage && this.page)?
			"Using this button will submit this page and move onto the next."
			:
			'Using this button will complete the survey and submit your responses.'
		;
	}

	get continueLabel(): string {
		if (this.page && this.onPage < this.totalPages) {
			return this.page.nextPageLabel;
		}

		return (this.hasNextPage && this.page)? this.page.nextPageLabel : ((this.publishedSurvey)? this.publishedSurvey.submitLabel : 'Submit');
	}

	get endPageContent(): string {
		// Because its currently plain-text we want to convert new lines to markup
		if (this.endPage === null) {
			return '';
		}

		return this.endPage.renderedContent.split('\n').join('<br/>');
	}

	get onSurveyPage(): boolean {
		return !!(this.page && this.onPage && !this.onEndPage && !this.onPasswordPage);
	}

	get responseGroup(): ResponseGroup | null {
		return this.responseStore.responseGroup;
	}

	get responseStore() {
		return useResponseStore();
	}

	get embedId(): string | null {
		if (!this.isEmbedded) {
			return null;
		}

		const id = this.$route.query['mdl-emd-id'];

		if (id === undefined) {
			return null;
		}

		return id as string;
	}

	get isEmbedded(): boolean {
		if (window.top === null) {
			return false;
		}

		return window.top !== window.self;
	}
}
</script>

<style lang="scss">
@media (max-width: 768px) {
	.survey {
		.survey-unavailable,
		.survey-complete,
		.survey-password,
		.survey-page {
			padding: 0 1rem;
			width: auto;

			padding-top: 3rem;
			padding-bottom: 3rem;
			margin: auto;

			.hdl-single-choice,
			.hdl-multiple-choice {
				.choices {
					.choice {
						padding-top: .5rem;
						padding-bottom: .5rem;
						min-width: auto;
						width: 100%;
					}
				}
			}

			.page-contents {
				.end {
					@apply flex-row;

					padding-bottom: 2rem;

					.hdl-button,
					button,
					input[type="submit"] {
						height: 46px;
					}

					button,
					input[type="submit"] {
						@apply flex-1;
					}
				}
			}
		}

		.page-header {
			padding-left: 1rem;
			padding-right: 1rem;
		}

		.hdl-badge-container {
			@apply justify-center border-t fixed bottom-0;
			border-color: var(--srv-border);
			padding-bottom: .2rem;
			padding-top: .2rem;

			backdrop-filter: blur(6px);

			width: 95%;
			left: 2.5%;

			.hdl-powered-badge {
				background: transparent;
			}
		}
	}
}

@media (min-width: 769px) and (max-width: 1200px) {
	.survey {
		margin: auto;

		.survey-unavailable,
		.survey-complete,
		.survey-password,
		.survey-page {
			padding: 0 1rem;
			width: var(--survey-width);

			margin: auto;
			padding-top: 6rem;
			padding-bottom: 6rem;

			&.with-header {
				padding-top: 2rem;
			}

			.page-contents {
				.end {
					padding-bottom: 6rem;
				}
			}
		}

		.page-header {
			padding-left: 1rem;
			padding-right: 1rem;
		}

		.survey-password {
			width: 500px;
		}

		.hdl-badge-container {
			@apply fixed inline-flex;
			right: 1rem;
			bottom: 1rem;
		}
	}
}

@media (min-width: 1201px) {
	.survey {
		margin: auto;

		.survey-unavailable,
		.survey-complete,
		.survey-password,
		.survey-page {
			width: var(--survey-width);

			margin: auto;
			padding-top: 6rem;
			padding-bottom: 6rem;

			&.with-header {
				padding-top: 2rem;
			}

			.page-contents {
				.end {
					padding-bottom: 6rem;
				}
			}
		}

		.page-header {
			padding-left: 2rem;
			padding-right: 2rem;
		}

		.survey-password {
			width: 500px;
		}

		.hdl-badge-container {
			@apply fixed inline-flex;
			right: 1rem;
			bottom: 1rem;
		}
	}
}

.hdl-new-version-available {
	@apply rounded mb-8 text-lg;

	background-color: var(--srv-hover-fill);
	color: var(--srv-text);

	a {
		@apply font-semibold;
		text-decoration: underline;
		color: var(--srv-text);
		cursor: pointer;
	}
}

.survey {
	.survey-unavailable,
	.survey-complete,
	.survey-password,
	.survey-page {
		//position: absolute;
		//top:0;
		//bottom: 0;
		//left: 0;
		//right: 0;

		.page-contents {
			.end {
				@apply mt-8;
			}
		}
	}

	color: var(--srv-text);
	font-family: var(--srv-body-font);

	h1, h2, h3, h4, h5, h6 {
		color: var(--srv-heading-color);
		font-family: var(--srv-heading-font);
	}

	.question {
		.question-label {
			color: var(--srv-title-color);
			font-family: var(--srv-title-font);
		}
	}

	.page-header {
		margin-top: 2rem;
		margin-bottom: 2rem;
	}

	.survey-unavailable,
	.survey-complete {
		.page-contents {
			@apply flex flex-col items-center text-lg;

			h1 {
				@apply font-semibold;
			}
		}
	}

	.create-your-own {
		@apply rounded bg-fill cursor-pointer py-2 px-4 text-primary select-none;

		border-left: 1px solid rgba(0, 0, 0, 0.15);
		border-right: 1px solid rgba(0, 0, 0, 0.15);
		border-top: 1px solid rgba(0, 0, 0, 0.15);
		border-bottom: 1px solid rgba(0, 0, 0, 0.25);
		box-shadow: rgba(0, 0, 0, 0.02) 0px 4px 4px -1px, rgba(0, 0, 0, 0.06) 0px 1px 1px 0px;

		&:hover {
			box-shadow: rgba(0, 0, 0, 0.05) 0px 4px 4px 0px, rgba(0, 0, 0, 0.1) 0px 1px 1px 0px;
		}

		&.selected {
			border-width: 1.5px;
			border-color: #000;
			box-shadow: #535abb25 0px 0px 0px 2px;
		}

		&:active {
			box-shadow: inset rgba(0, 0, 0, 0.05) 0px 4px 4px 0px;
		}
	}

	.hdl-powered-badge {
		@apply border border-transparent rounded cursor-pointer p-2;

		font-family: Inter, sans-serif;

		background-color: var(--srv-hover-fill);
		backdrop-filter: blur(6px);
		color: var(--srv-text);

		&:visited {
			color: var(--srv-text);
		}

		&:hover {
			border-color: var(--srv-divider);
		}
	}

	.survey-password {
		.errors {
			@apply text-hdl-wc-red;
		}
	}

	// OVERRIDES
	.input-box-borders {
		border-color: var(--srv-border);

		&:focus-within,
		&:hover {
			border-color: rgba(var(--srv-border),.25);
		}

		&.selected {
			border-color: var(--srv-accent);
		}
	}

	a {
		color: var(--srv-text);

		&:hover,
		&:visited {
			color: var(--srv-text);
		}
	}
}

body.mdl-survey-transparent {
	background-color: transparent !important;
}

body.mdl-survey-no-spacing {
	.survey {
		.survey-unavailable,
		.survey-complete,
		.survey-password,
		.survey-page {
			padding-top: 1rem !important;
			padding-bottom: 1rem !important;
		}

		&.has-progress-bar {
			.survey-page {
				padding-top: 3rem !important;
			}
		}
	}
}
</style>
